import { create } from 'zustand';

export interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  category: string;
  imageUrl: string;
  isAvailable: boolean;
  prepTime: string;
}

export interface Promotion {
  id: string;
  title: string;
  description: string;
  /**
   * ⚠️ DECÍA `MARTES_LOCO`, Y ESE VALOR NO EXISTE EN NINGUNA PARTE.
   *
   * El panel del negocio guarda `VIERNES_LOCO` y el servidor
   * (functions/src/promos.ts, functions/src/promocionesDeNegocio.ts) busca
   * `VIERNES_LOCO`. El tipo se quedó con un nombre de una versión anterior,
   * así que TypeScript estaba validando contra una lista que no se parecía a
   * los datos: cualquier comparación con el valor real daba error de tipos y
   * la de `MARTES_LOCO` —que nunca puede ser cierta— compilaba.
   */
  type: 'DELIVERY_GRATIS' | 'DESCUENTO' | 'VIERNES_LOCO';
  // Los números los pone el negocio en su oferta. Ver
  // `constants/promocionesDeNegocio.ts` para qué hace cada uno.
  minimo?: number;
  porcentaje?: number;
  monto?: number;
  soloPrimerPedido?: boolean;
  /** @deprecated Lo escribía el panel viejo y nadie lo leía. Usar `porcentaje` o `monto`. */
  discountValue?: number;
  icon: string;
  color: string;
}

export interface Restaurant {
  id: string;
  name: string;
  description: string;
  logoUrl: string;
  coverUrl: string;
  rating: number;
  reviews: number;
  deliveryTime: string;
  deliveryFee: number;
  category: string;
  menu: Product[];
  isPremium?: boolean;
  activePromotions?: Promotion[];
}

export interface CartItem {
  product: Product;
  quantity: number;
  notes?: string;
}

interface FoodState {
  restaurants: Restaurant[];
  cart: CartItem[];
  cartRestaurantId: string | null; // Solo se puede pedir de un restaurante a la vez
  
  // Acciones
  setRestaurants: (restaurants: Restaurant[]) => void;
  addToCart: (product: Product, restaurantId: string, quantity?: number, notes?: string) => void;
  /** Recibe la clave de `claveProducto(item.product)`, no el id crudo. */
  removeFromCart: (claveDelProducto: string) => void;
  clearCart: () => void;
  getCartTotal: () => number;
}

/**
 * Clave con la que el carrito distingue un plato de otro.
 *
 * POR QUÉ NO BASTA `product.id`
 * El panel del restaurante guardaba los platos SIN `id` (el formulario nunca
 * generaba uno). Con dos platos sin id, `a.id === b.id` es
 * `undefined === undefined` → **true**: el cliente agregaba una pizza y una
 * hamburguesa y el carrito las fusionaba en "2x pizza". Y `removeFromCart`
 * borraba el carrito entero de un golpe por la misma razón.
 *
 * El escritor ya está arreglado (`restaurant-dashboard.tsx` genera el id),
 * pero los menús que YA están guardados en Firestore siguen sin él. Por eso
 * el lector tiene que ser tolerante: si no hay id, distinguimos por nombre +
 * precio, que es lo que ve el cliente.
 */
export function claveProducto(p: Pick<Product, 'id' | 'name' | 'price'>): string {
  const id = String(p?.id ?? '').trim();
  if (id) return `id:${id}`;
  return `np:${String(p?.name ?? '').trim().toLowerCase()}|${p?.price ?? ''}`;
}

export const useFoodStore = create<FoodState>((set, get) => ({
  restaurants: [],
  cart: [],
  cartRestaurantId: null,

  setRestaurants: (restaurants) => set({ restaurants }),
  
  addToCart: (product, restaurantId, quantity = 1, notes = '') => {
    set((state) => {
      // Si el carrito está vacío, asigna el restaurante actual
      if (state.cart.length === 0) {
        return {
          cartRestaurantId: restaurantId,
          cart: [{ product, quantity, notes }]
        };
      }

      // Si intenta agregar de otro restaurante, rechazar (o limpiar)
      if (state.cartRestaurantId !== restaurantId) {
        // En una app real podríamos mostrar una alerta preguntando si quiere limpiar el carrito
        console.warn('Solo puedes pedir de un restaurante a la vez');
        return state;
      }

      // Si el producto ya está, incrementar cantidad
      const clave = claveProducto(product);
      const existingItem = state.cart.find(item => claveProducto(item.product) === clave);
      if (existingItem) {
        return {
          cart: state.cart.map(item =>
            claveProducto(item.product) === clave
              ? { ...item, quantity: item.quantity + quantity, notes }
              : item
          )
        };
      }

      // Producto nuevo en el carrito
      return {
        cart: [...state.cart, { product, quantity, notes }]
      };
    });
  },

  // Recibe la CLAVE del producto (`claveProducto`), no el id pelado: con
  // menús viejos sin id, filtrar por id borraba todo el carrito de un toque.
  removeFromCart: (clave) => {
    set((state) => {
      const newCart = state.cart.filter(item => claveProducto(item.product) !== clave);
      return {
        cart: newCart,
        cartRestaurantId: newCart.length === 0 ? null : state.cartRestaurantId
      };
    });
  },

  clearCart: () => set({ cart: [], cartRestaurantId: null }),

  getCartTotal: () => {
    return get().cart.reduce((total, item) => total + (item.product.price * item.quantity), 0);
  }
}));
